import React, { useState, useEffect, useCallback, useRef, useContext } from 'react';
import { ConfirmModal } from '../../ui/ConfirmModal';
import { Search, MessageCircle, CheckCircle, Clock, AlertCircle, Bot, BotOff, User, Loader2, RefreshCw, Send, ImagePlus, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { formatDistanceToNow } from 'date-fns';
import { vi } from 'date-fns/locale';
import { workerApi } from '../../../services/workerApi';
import type { WorkerConversation, WorkerMessage } from '../../../types/platform';
import { ChatbotContext } from '../../../context/ChatbotContext';

interface ConversationsTabProps {
  accountId: string;   // Facebook Page ID (platform.platform_id) used to filter conversations
}

type FilterStatus = 'all' | 'active' | 'archived';

const STATUS_MAP = {
  active:   { labelKey: 'statusOpen',     icon: <MessageCircle size={11} />, cls: 'bg-[#3390ec]/10 text-[#3390ec]' },
  archived: { labelKey: 'statusArchived', icon: <CheckCircle   size={11} />, cls: 'bg-emerald-50 text-emerald-600' },
  spam:     { labelKey: 'statusSpam',     icon: <Clock          size={11} />, cls: 'bg-amber-50 text-amber-600' },
};

const senderRole = (type: string, t: (key: string) => string) =>
  type === 'customer' ? t('roleCustomer') : type === 'bot' ? t('roleBot') : t('roleAgent');

function tsToDate(ts: number): Date {
  // Worker stores unix timestamps; multiply by 1000 for ms if < year 9999
  return new Date(ts > 1e10 ? ts : ts * 1000);
}

const ConversationItem = ({
  conv,
  isSelected,
  onClick,
}: {
  conv: WorkerConversation;
  isSelected: boolean;
  onClick: () => void;
}) => {
  const { t } = useTranslation('conversation');
  const st = STATUS_MAP[conv.status as keyof typeof STATUS_MAP] ?? STATUS_MAP.active;
  const timeAgo = formatDistanceToNow(tsToDate(conv.last_message_at), { addSuffix: true, locale: vi });
  const avatar =
    conv.customer_avatar ||
    `https://api.dicebear.com/7.x/thumbs/svg?seed=${encodeURIComponent(conv.user_id)}`;

  // Parse interest_level from metadata JSON
  let interestLevel = conv.interest_level ?? '';
  if (!interestLevel && conv.metadata) {
    try {
      const meta = JSON.parse(conv.metadata);
      interestLevel = meta?.interest_level ?? '';
    } catch {
      // ignore invalid JSON
    }
  }

  return (
    <button
      type="button"
      onClick={onClick}
      className={`w-full flex items-start gap-3 px-4 py-3 text-left transition-colors ${
        isSelected ? 'bg-[#3390ec]/5 border-l-2 border-[#3390ec]' : 'border-l-2 border-transparent hover:bg-gray-50'
      }`}
    >
      <img
        src={avatar}
        alt={conv.customer_name || conv.user_id}
        className="w-10 h-10 rounded-full flex-shrink-0 bg-gray-100 object-cover"
      />
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2">
          <span className="text-sm font-semibold text-gray-800 truncate">
            {conv.customer_name || conv.user_id}
          </span>
          <span className="text-[11px] text-gray-400 shrink-0">{timeAgo}</span>
        </div>
        <p className="text-xs text-gray-500 truncate mt-0.5">{conv.last_message || t('noMessagesPreview')}</p>
        <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
          <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium ${st.cls}`}>
            {st.icon} {t(st.labelKey)}
          </span>
          {conv.unread_count > 0 && (
            <span className="bg-[#3390ec] text-white text-[10px] font-bold rounded-full w-4 h-4 flex items-center justify-center">
              {conv.unread_count}
            </span>
          )}
          {!!conv.is_bot_handled && (
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-purple-50 text-purple-600">
              <Bot size={9} /> Bot
            </span>
          )}
          {interestLevel && (
            <span
              title={t('interestLevel')}
              className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-50 text-amber-700 max-w-[120px] truncate"
            >
              {interestLevel}
            </span>
          )}
        </div>
      </div>
    </button>
  );
};

const MessageBubble = ({ msg }: { msg: WorkerMessage }) => {
  const { t } = useTranslation('conversation');
  const isOutgoing = msg.sender_type === 'agent' || msg.sender_type === 'bot';
  const timeStr = tsToDate(msg.sent_at).toLocaleTimeString('vi-VN', { hour: '2-digit', minute: '2-digit' });

  const bubbleCls = isOutgoing
    ? 'bg-[#3390ec] text-white rounded-br-sm'
    : 'bg-white border border-gray-100 text-gray-800 rounded-bl-sm';

  const senderLabel = isOutgoing && (
    <p className="text-[10px] mb-1 font-semibold text-white/70">{senderRole(msg.sender_type, t)}</p>
  );
  const timestamp = (
    <p className={`text-[10px] mt-1 text-right ${isOutgoing ? 'text-white/70' : 'text-gray-400'}`}>{timeStr}</p>
  );

  // Image message
  if (msg.content_type === 'image' && msg.content_url) {
    return (
      <div className={`flex ${isOutgoing ? 'justify-end' : 'justify-start'}`}>
        <div className={`max-w-[60%] rounded-2xl overflow-hidden ${isOutgoing ? 'rounded-br-sm' : 'rounded-bl-sm'}`}>
          {senderLabel && (
            <div className={`px-3 pt-2 ${isOutgoing ? 'bg-[#3390ec]' : 'bg-white border border-b-0 border-gray-100'}`}>
              {senderLabel}
            </div>
          )}
          <a href={msg.content_url} target="_blank" rel="noopener noreferrer">
            <img
              src={msg.content_url}
              alt={t('imageAlt')}
              className="block w-full max-h-72 object-cover"
              onError={(e) => { (e.target as HTMLImageElement).style.opacity = '0.3'; }}
            />
          </a>
          <div className={`px-2 pb-1 ${isOutgoing ? 'bg-[#3390ec]' : 'bg-white border border-t-0 border-gray-100'}`}>
            {timestamp}
          </div>
        </div>
      </div>
    );
  }

  // Carousel message (multiple images from Generic Template)
  if (msg.content_type === 'carousel') {
    let elements: { title?: string; image_url?: string; subtitle?: string }[] = [];
    try {
      const meta = typeof msg.content_metadata === 'string'
        ? JSON.parse(msg.content_metadata || '{}')
        : (msg.content_metadata || {});
      elements = meta.elements || [];
    } catch { /* ignore */ }
    if (elements.length > 0) {
      return (
        <div className={`flex ${isOutgoing ? 'justify-end' : 'justify-start'}`}>
          <div className="max-w-[85%]">
            {isOutgoing && <p className="text-[10px] mb-1 font-semibold text-gray-400 text-right">{senderRole(msg.sender_type, t)}</p>}
            <div className="flex gap-2 overflow-x-auto pb-1">
              {elements.map((el, i) => (
                <a
                  key={i}
                  href={el.image_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-shrink-0 w-36 rounded-xl overflow-hidden border border-gray-100 bg-white shadow-sm"
                >
                  {el.image_url && (
                    <img
                      src={el.image_url}
                      alt={el.title || t('carouselAlt', { index: i + 1 })}
                      className="w-full h-36 object-cover"
                      onError={(e) => { (e.target as HTMLImageElement).style.opacity = '0.3'; }}
                    />
                  )}
                  {el.title && (
                    <p className="text-xs font-medium text-gray-700 px-2 py-1.5 truncate">{el.title}</p>
                  )}
                </a>
              ))}
            </div>
            <p className={`text-[10px] mt-0.5 ${isOutgoing ? 'text-right text-gray-400' : 'text-gray-400'}`}>{timeStr}</p>
          </div>
        </div>
      );
    }
  }

  // Text / quick_reply / fallback
  return (
    <div className={`flex ${isOutgoing ? 'justify-end' : 'justify-start'}`}>
      <div className={`max-w-[80%] px-3.5 py-2 rounded-2xl text-sm ${bubbleCls}`}>
        {senderLabel}
        <p className="whitespace-pre-wrap break-words">
          {msg.content_text || <span className="italic text-xs opacity-60">[{msg.content_type}]</span>}
        </p>
        {timestamp}
      </div>
    </div>
  );
};

const FILTER_TABS: { key: FilterStatus; labelKey: string }[] = [
  { key: 'all',      labelKey: 'filterAll' },
  { key: 'active',   labelKey: 'filterOpen' },
  { key: 'archived', labelKey: 'filterDone' },
];

export const ConversationsTab: React.FC<ConversationsTabProps> = ({ accountId }) => {
  const { t } = useTranslation('conversation');
  const { workerUrl, jwtToken } = useContext(ChatbotContext);
  const [conversations, setConversations] = useState<WorkerConversation[]>([]);
  const [messages, setMessages] = useState<WorkerMessage[]>([]);
  const [filter, setFilter] = useState<FilterStatus>('all');
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [loadingConvs, setLoadingConvs] = useState(true);
  const [loadingMsgs, setLoadingMsgs] = useState(false);
  const [convError, setConvError] = useState(false);
  const [sendText, setSendText] = useState('');
  const [sending, setSending] = useState(false);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [togglingControl, setTogglingControl] = useState(false);
  const [controlModal, setControlModal] = useState<{ open: boolean; targetAgentControlled: boolean }>(
    { open: false, targetAgentControlled: false }
  );
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const fetchConversations = useCallback(async () => {
    setLoadingConvs(true);
    setConvError(false);
    try {
      const params: any = { limit: 100 };
      if (filter !== 'all') params.status = filter;
      const res = await workerApi.getConversations({ ...params, channel_id: accountId });
      setConversations(res.conversations || []);
    } catch (e) {
      console.error('Failed to fetch conversations:', e);
      setConvError(true);
    } finally {
      setLoadingConvs(false);
    }
  }, [accountId, filter]);

  useEffect(() => { fetchConversations(); }, [fetchConversations, workerUrl, jwtToken]);

  const fetchMessages = useCallback(async (convId: string, quiet = false) => {
    if (!quiet) setLoadingMsgs(true);
    try {
      const res = await workerApi.getMessages(convId, { limit: 1000 });
      setMessages(res.messages || []);
    } catch (e) {
      console.error('Failed to fetch messages:', e);
      if (!quiet) setMessages([]);
    } finally {
      if (!quiet) setLoadingMsgs(false);
    }
  }, []);

  const handleSelect = async (convId: string) => {
    setSelectedId(convId);
    setSendText('');
    await fetchMessages(convId);
    // Reset unread badge locally + tell worker to clear unread_count
    setConversations((prev) =>
      prev.map((c) => c.id === convId ? { ...c, unread_count: 0 } : c)
    );
    workerApi.markConversationRead(convId).catch(() => {/* non-critical */});
  };

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = async () => {
    if (!sendText.trim() || !selected || sending) return;
    const text = sendText.trim();
    setSendText('');
    setSending(true);
    // Optimistic bubble
    const tempMsg: WorkerMessage = {
      id: `temp-${Date.now()}`,
      conversation_id: selected.id,
      platform_message_id: '',
      sender_type: 'agent',
      sender_id: 'agent',
      content_type: 'text',
      content_text: text,
      content_url: '',
      content_metadata: '{}',
      is_read: 1,
      sent_at: Date.now() / 1000,
      created_at: Date.now() / 1000,
    };
    setMessages((prev) => [...prev, tempMsg]);
    try {
      await workerApi.sendAgentMessage({
        channel_id: accountId,
        user_id: selected.user_id,
        message_text: text,
        conversation_id: selected.id,
      });
      // Background refresh after worker processes the message
      setTimeout(() => fetchMessages(selected.id, true), 1500);
    } catch (err) {
      console.error('Failed to send message:', err);
      setMessages((prev) => prev.filter((m) => m.id !== tempMsg.id));
    } finally {
      setSending(false);
    }
  };

  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !file.type.startsWith('image/')) return;
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
    // Reset input so re-selecting the same file triggers onChange
    e.target.value = '';
  };

  const handleCancelImage = () => {
    if (imagePreview) URL.revokeObjectURL(imagePreview);
    setImageFile(null);
    setImagePreview(null);
  };

  const handleSendImage = async () => {
    if (!imageFile || !selected || sending) return;
    setSending(true);
    // Optimistic bubble with local preview
    const tempMsg: WorkerMessage = {
      id: `temp-img-${Date.now()}`,
      conversation_id: selected.id,
      platform_message_id: '',
      sender_type: 'agent',
      sender_id: 'agent',
      content_type: 'image',
      content_text: '',
      content_url: imagePreview || '',
      content_metadata: '{}',
      is_read: 1,
      sent_at: Date.now() / 1000,
      created_at: Date.now() / 1000,
    };
    setMessages((prev) => [...prev, tempMsg]);
    handleCancelImage();
    try {
      await workerApi.sendAgentImage({
        channel_id: accountId,
        user_id: selected.user_id,
        conversation_id: selected.id,
        file: imageFile,
      });
      // Longer delay: echo must arrive and be processed by worker before refresh
      setTimeout(() => fetchMessages(selected.id, true), 3000);
    } catch (err) {
      console.error('Failed to send image:', err);
      setMessages((prev) => prev.filter((m) => m.id !== tempMsg.id));
    } finally {
      setSending(false);
    }
  };

  const handleToggleControl = () => {
    if (!selected || togglingControl) return;
    const newAgentControlled = !!selected.is_bot_handled; // true = bot currently → agent takes over
    setControlModal({ open: true, targetAgentControlled: newAgentControlled });
  };

  const handleConfirmControl = async () => {
    if (!selected) return;
    const newAgentControlled = controlModal.targetAgentControlled;
    setControlModal((m) => ({ ...m, open: false }));
    setTogglingControl(true);
    // Optimistic update
    setConversations((prev) =>
      prev.map((c) =>
        c.id === selected.id
          ? { ...c, is_bot_handled: newAgentControlled ? 0 : 1 }
          : c
      )
    );
    try {
      await workerApi.setAgentControl({
        conversation_id: selected.id,
        channel_id: accountId,
        agent_controlled: newAgentControlled,
      });
    } catch (err) {
      console.error('Failed to toggle control:', err);
      // Revert optimistic update on error
      setConversations((prev) =>
        prev.map((c) =>
          c.id === selected.id
            ? { ...c, is_bot_handled: selected.is_bot_handled }
            : c
        )
      );
    } finally {
      setTogglingControl(false);
    }
  };

  const filtered = conversations.filter((c) => {
    if (search && !c.customer_name?.toLowerCase().includes(search.toLowerCase()) &&
        !c.last_message?.toLowerCase().includes(search.toLowerCase()) &&
        !c.user_id.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const selected = conversations.find((c) => c.id === selectedId) ?? null;

  return (
    <>
    <div className="flex gap-4 h-full min-h-0">
      {/* Left: Conversation List */}
      <div className="w-64 flex-shrink-0 bg-white rounded-xl border border-gray-100 flex flex-col overflow-hidden">
        {/* Search + refresh */}
        <div className="p-3 border-b border-gray-50 flex items-center gap-2">
          <div className="flex items-center gap-2 bg-gray-50 rounded-lg px-3 py-2 flex-1">
            <Search size={13} className="text-gray-400 shrink-0" />
            <input
              type="text"
              placeholder={t('searchConversation')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="flex-1 bg-transparent text-sm outline-none text-gray-700 placeholder-gray-400 min-w-0"
            />
          </div>
          <button
            title={t('reload')}
            onClick={fetchConversations}
            className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <RefreshCw size={13} className={loadingConvs ? 'animate-spin' : ''} />
          </button>
        </div>

        {/* Filter tabs */}
        <div className="flex gap-1 px-3 py-2 border-b border-gray-50 overflow-x-auto">
          {FILTER_TABS.map((tab) => (
            <button
              key={tab.key}
              type="button"
              onClick={() => setFilter(tab.key)}
              className={`shrink-0 px-2.5 py-1 rounded-full text-[11px] font-medium transition-colors ${
                filter === tab.key ? 'bg-[#3390ec] text-white' : 'text-gray-500 hover:bg-gray-100'
              }`}
            >
              {t(tab.labelKey)}
            </button>
          ))}
        </div>

        {/* List */}
        <div className="flex-1 overflow-y-auto">
          {loadingConvs ? (
            <div className="flex flex-col items-center justify-center h-32 text-gray-300 gap-2">
              <Loader2 size={20} className="animate-spin" />
              <p className="text-xs">{t('loading')}</p>
            </div>
          ) : convError ? (
            <div className="flex flex-col items-center justify-center h-32 text-gray-300 gap-2 px-4 text-center">
              <AlertCircle size={20} />
              <p className="text-xs">{t('noConversationWorker')}</p>
            </div>
          ) : filtered.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-32 text-gray-300 gap-2">
              <MessageCircle size={24} />
              <p className="text-xs">{t('noConversations')}</p>
            </div>
          ) : (
            filtered.map((c) => (
              <ConversationItem
                key={c.id}
                conv={c}
                isSelected={c.id === selectedId}
                onClick={() => handleSelect(c.id)}
              />
            ))
          )}
        </div>
      </div>

      {/* Right: Message thread */}
      <div className="flex-1 bg-white rounded-xl border border-gray-100 flex flex-col overflow-hidden min-w-0">
        {!selected ? (
          <div className="flex flex-col items-center justify-center h-full text-gray-300 gap-3">
            <MessageCircle size={40} strokeWidth={1} />
            <p className="text-sm">{t('selectConversation')}</p>
          </div>
        ) : (
          <>
            {/* Header */}
            <div className="flex items-center gap-3 px-4 py-3 border-b border-gray-100 flex-shrink-0">
              <img
                src={selected.customer_avatar || `https://api.dicebear.com/7.x/thumbs/svg?seed=${selected.user_id}`}
                alt={selected.customer_name}
                className="w-8 h-8 rounded-full bg-gray-100 object-cover"
              />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-gray-800 truncate">
                  {selected.customer_name || selected.user_id}
                </p>
                <p className="text-xs text-gray-400 font-mono truncate">{selected.user_id}</p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {/* Status badge */}
                {!!selected.is_bot_handled
                  ? <span className="inline-flex items-center gap-1 px-2 py-1 bg-purple-50 text-purple-600 rounded-lg text-xs font-medium"><Bot size={11}/> {t('roleBot')}</span>
                  : <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-50 text-blue-600 rounded-lg text-xs font-medium"><User size={11}/> {t('roleAgent')}</span>
                }
                {/* Control toggle */}
                <button
                  type="button"
                  title={!!selected.is_bot_handled ? t('agentTakeOver') : t('returnToBot')}
                  onClick={handleToggleControl}
                  disabled={togglingControl}
                  className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                    !!selected.is_bot_handled
                      ? 'bg-amber-50 text-amber-600 hover:bg-amber-100'
                      : 'bg-purple-50 text-purple-600 hover:bg-purple-100'
                  }`}
                >
                  {togglingControl
                    ? <Loader2 size={11} className="animate-spin" />
                    : !!selected.is_bot_handled
                      ? <><BotOff size={11}/> {t('agentTakeOver')}</>
                      : <><Bot size={11}/> {t('returnToBot')}</>
                  }
                </button>
              </div>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {loadingMsgs ? (
                <div className="flex items-center justify-center h-32 text-gray-300">
                  <Loader2 size={24} className="animate-spin" />
                </div>
              ) : messages.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-32 text-gray-300 gap-2">
                  <MessageCircle size={24} strokeWidth={1} />
                  <p className="text-xs">{t('noMessages')}</p>
                </div>
              ) : (
                messages.map((m) => <MessageBubble key={m.id} msg={m} />)
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Message Input */}
            <div className="border-t border-gray-100 p-3 flex-shrink-0 bg-white">
              {!!selected.is_bot_handled ? (
                <div className="flex items-center justify-center gap-2 py-2 text-xs text-gray-400">
                  <Bot size={14} />
                  <span>{t('botControlNote')}</span>
                </div>
              ) : (
              <div className="space-y-2">
                {/* Image preview */}
                {imagePreview && (
                  <div className="relative inline-block">
                    <img
                      src={imagePreview}
                      alt="preview"
                      className="max-h-32 rounded-lg border border-gray-200 object-cover"
                    />
                    <button
                      onClick={handleCancelImage}
                      className="absolute -top-1.5 -right-1.5 w-5 h-5 flex items-center justify-center rounded-full bg-gray-700 text-white hover:bg-gray-900 transition-colors"
                    >
                      <X size={12} />
                    </button>
                  </div>
                )}
                <div className="flex items-end gap-2">
                  {/* Hidden file input */}
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleImageSelect}
                    className="hidden"
                  />
                  {/* Image picker button */}
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    disabled={sending}
                    title={t('sendImage')}
                    className="flex-shrink-0 w-9 h-9 flex items-center justify-center rounded-xl border border-gray-200 text-gray-400 hover:text-[#3390ec] hover:border-[#3390ec] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    <ImagePlus size={16} />
                  </button>
                  <textarea
                    value={sendText}
                    onChange={(e) => setSendText(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); imageFile ? handleSendImage() : handleSend(); }
                    }}
                    placeholder={t('typePlaceholder')}
                    rows={1}
                    className="flex-1 resize-none rounded-xl border border-gray-200 px-3 py-2 text-sm outline-none focus:border-[#3390ec] transition-colors max-h-32 overflow-y-auto"
                  />
                  <button
                    onClick={imageFile ? handleSendImage : handleSend}
                    disabled={(!sendText.trim() && !imageFile) || sending}
                    className="flex-shrink-0 w-9 h-9 flex items-center justify-center rounded-xl bg-[#3390ec] text-white hover:bg-[#2478d4] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    {sending ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />}
                  </button>
                </div>
              </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>

    {/* ── Agent-control confirm modal ─────────────────────────────────────── */}
    <ConfirmModal
      open={controlModal.open}
      title={controlModal.targetAgentControlled ? `🧑 ${t('agentControlTitle')}` : `🤖 ${t('botControlTitle')}`}
      description={
        controlModal.targetAgentControlled ? (
          <div className="space-y-2.5">
            <p>{t('confirmAfter')}</p>
            <ul className="list-disc list-inside space-y-1.5 text-gray-500">
              <li><span className="text-gray-700 font-medium">{t('agentCtrl1Bold')}</span> {t('agentCtrl1Rest')}</li>
              <li>{t('agentCtrl2')}</li>
              <li>{t('agentCtrl3')}</li>
              <li>{t('agentCtrl4')}</li>
            </ul>
          </div>
        ) : (
          <div className="space-y-2.5">
            <p>{t('confirmAfter')}</p>
            <ul className="list-disc list-inside space-y-1.5 text-gray-500">
              <li><span className="text-gray-700 font-medium">{t('botCtrl1Bold')}</span> {t('botCtrl1Rest')}</li>
              <li>{t('botCtrl2')}</li>
              <li>{t('botCtrl3')}</li>
            </ul>
          </div>
        )
      }
      confirmLabel={controlModal.targetAgentControlled ? t('confirmTakeControl') : t('returnToBot')}
      confirmVariant={controlModal.targetAgentControlled ? 'warning' : 'primary'}
      loading={togglingControl}
      onConfirm={handleConfirmControl}
      onCancel={() => setControlModal((m) => ({ ...m, open: false }))}
    />
    </>
  );
};
